import { EChartsOption } from 'echarts';
import {
  AQISensor,
  AQISensorObservation,
} from 'features/aqiSensors/aqiSensors.types';
import { useTranslation } from 'react-i18next';
import { formatDate, getDateFormatted } from 'shared/dates';
import { formatNumber } from 'shared/utils';
import { alpha, useTheme } from '@mui/material';
import {
  getChartBounds,
  getLevelsMarkAreaData,
  observationToSeriesData,
} from './useAQILevelsChart.utils';
import { chartWatermarkBase64 } from './chartWatermarkBase64';

type GetEchartsOptionsArgs = {
  observations: AQISensorObservation[];
  aqiSensor?: AQISensor;
  minTimestamp: Date;
  maxTimestamp: Date;
};

export const useAQILevelsChart = (
  props: GetEchartsOptionsArgs,
): EChartsOption | null => {
  const { observations, aqiSensor, minTimestamp, maxTimestamp } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  if (!aqiSensor) {
    return null;
  }

  const echartsObservationValues = observations.map(observationToSeriesData);

  const { upperBounds, lowerBounds } = getChartBounds(observations);

  const commonSeriesValues = {
    type: 'line' as const,
    smooth: true,
    showSymbol: false,
    encode: {
      x: 'timestamp',
      y: 'value',
    },
  };

  return {
    animation: false,
    grid: {
      top: 20,
      bottom: 30,
      left: 40,
      right: 16,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      // Prevent tooltip from covering sticky header and other elements outside of the chart
      extraCssText: 'z-index: 0',
      formatter: (params) => {
        const { value } = Array.isArray(params) ? params[0] : params;
        const [date, level] = value as [Date, number];
        const timestamp = getDateFormatted(date);
        const levelFormatted = formatNumber(level, 0);

        return `
            <div>
              <div><strong>${timestamp}</strong></div>
              <div>${levelFormatted}</div>
            </div>
          `;
      },
    },
    dataset: [
      {
        source: echartsObservationValues,
        dimensions: ['timestamp', 'value'],
      },
    ],
    xAxis: {
      type: 'time',
      min: minTimestamp,
      max: maxTimestamp,
      splitNumber: 3,
      axisLabel: {
        formatter: (value) => formatDate(new Date(value), 'h a'),
        hideOverlap: true,
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: 10,
      },
      axisPointer: {
        snap: true,
        lineStyle: {
          color: theme.palette.text.primary,
          width: 2,
        },
        label: {
          show: false,
        },
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: theme.palette.text.primary,
        },
      },
    },
    yAxis: {
      min: lowerBounds,
      max: upperBounds,
      axisLabel: {
        formatter: (value: number) => formatNumber(value, 0),
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: 10,
      },
      axisPointer: {
        type: 'none',
        triggerOn: 'none',
        snap: false,
        label: {
          show: false,
        },
      },
      splitLine: {
        lineStyle: {
          color: alpha(theme.palette.text.disabled, 0.25),
        },
      },
      interval: 25,
      axisLine: {
        lineStyle: {
          color: theme.palette.text.primary,
        },
      },
    },
    series: [
      {
        ...commonSeriesValues,
        datasetIndex: 0,
        markArea: {
          silent: true,
          emphasis: {
            disabled: true,
          },
          label: {
            fontSize: 12,
            textBorderColor: 'transparent',
            position: 'insideBottomLeft',
            color: alpha(theme.palette.text.primary, 0.75),
          },
          data: getLevelsMarkAreaData(t, upperBounds),
        },
        markLine: undefined,
      },
    ],
    graphic: [
      {
        type: 'image',
        style: {
          image: chartWatermarkBase64,
          width: 200,
          opacity: 0.5,
        },
        left: 'center',
        top: 'center',
        right: 'center',
        bottom: 'center',
        z: -1, // Places it behind other chart elements
      },
    ],
  };
};
